import * as React from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import axiosInstance from 'utils/axiosInterceptors';
import { TLocations, TShelves } from 'utils/types';
import GenericShelfGrid from './GenericShelfGrid';
import SecondFloorMap from './SecondFloorMap';
import StorefrontMap from './StorefrontMap';
import ShelfItemsPanel from './ShelfItemsPanel';
import {
  FloorSectionId,
  SECOND_FLOOR_SECTIONS,
  hasSecondFloorShelves,
  hasStorefrontShelves,
  isSecondFloorShelfCode,
  isStorefrontLocationName
} from './floorLayouts';

type LocationMapViewProps = {
  branchDomain?: string;
  locations: TLocations[];
  locationsLoading?: boolean;
  selectedLocationId: number | null;
  onSelectLocation: (locationId: number | null) => void;
  isAdmin?: boolean;
};

type SelectedShelf = {
  code: string;
  shelf?: TShelves;
};

const LocationMapView = ({
  branchDomain,
  locations,
  locationsLoading,
  selectedLocationId,
  onSelectLocation,
  isAdmin
}: LocationMapViewProps) => {
  const [shelves, setShelves] = React.useState<TShelves[]>([]);
  const [shelvesLoading, setShelvesLoading] = React.useState(false);
  const [sectionId, setSectionId] = React.useState<FloorSectionId | 'other' | null>(
    null
  );
  const [selectedShelf, setSelectedShelf] = React.useState<SelectedShelf | null>(
    null
  );

  const selectedLocation =
    locations.find(location => location.locationId === selectedLocationId) ??
    null;

  React.useEffect(() => {
    setSelectedShelf(null);
    setSectionId(null);
    if (selectedLocationId) setShelvesLoading(true);
  }, [selectedLocationId]);

  React.useEffect(() => {
    if (!branchDomain || !selectedLocationId) {
      setShelves([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setShelvesLoading(true);
      try {
        const { data } = await axiosInstance.get(
          `/api/v1/${branchDomain}/locations/${selectedLocationId}/shelves`,
          { params: { page: 1, pageSize: 9999 } }
        );
        if (cancelled) return;
        const rows = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.Data)
            ? data.Data
            : [];
        setShelves(rows);
      } catch {
        if (!cancelled) setShelves([]);
      } finally {
        if (!cancelled) setShelvesLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [branchDomain, selectedLocationId]);

  const shelvesByCode = React.useMemo(
    () =>
      Object.fromEntries(
        shelves.map(shelf => [shelf.shelfCode.toUpperCase(), shelf])
      ) as Record<string, TShelves>,
    [shelves]
  );

  const otherShelves = shelves.filter(
    shelf => !isSecondFloorShelfCode(shelf.shelfCode)
  );
  const showSecondFloor = hasSecondFloorShelves(
    shelves.map(shelf => shelf.shelfCode)
  );
  const showStorefrontMap =
    isStorefrontLocationName(selectedLocation?.locationName) ||
    hasStorefrontShelves(shelves.map(shelf => shelf.shelfCode));

  if (!selectedLocation) {
    if (locationsLoading) {
      return (
        <div className="text-center py-8">
          <Spinner animation="border" />
        </div>
      );
    }

    if (locations.length === 0) {
      return (
        <div className="text-body-tertiary py-8 text-center">
          No locations yet.
        </div>
      );
    }

    return (
      <Row className="g-3">
        {locations.map(location => (
          <Col key={location.locationId} xs={12} md={6} xl={4}>
            <Card
              className="h-100 warehouse-location-card"
              role="button"
              tabIndex={0}
              onClick={() => onSelectLocation(location.locationId)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectLocation(location.locationId);
                }
              }}
            >
              <Card.Body className="d-flex align-items-center gap-3">
                <div className="warehouse-location-icon">
                  <FontAwesomeIcon icon={faMapLocationDot} />
                </div>
                <div>
                  <div className="fw-semibold fs-7">{location.locationName}</div>
                  <div className="text-body-tertiary small">
                    Open shelf map
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button
          variant="phoenix-secondary"
          size="sm"
          startIcon={<FontAwesomeIcon icon={faChevronLeft} />}
          onClick={() => {
            if (showSecondFloor && sectionId) {
              setSectionId(null);
              setSelectedShelf(null);
              return;
            }
            onSelectLocation(null);
          }}
        >
          Back
        </Button>
        <h5 className="mb-0">{selectedLocation.locationName}</h5>
        {isAdmin && (
          <Link
            to={`/location-details/${selectedLocation.locationId}`}
            className="ms-auto fs-9"
          >
            Manage shelves
          </Link>
        )}
      </div>

      {shelvesLoading && shelves.length === 0 ? (
        <div className="text-center py-8">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="d-flex flex-wrap gap-4 align-items-start">
          <div className="flex-grow-1">
            {showStorefrontMap &&
              (shelvesLoading ? (
                <div className="text-center py-8">
                  <Spinner animation="border" />
                </div>
              ) : (
                <StorefrontMap
                  shelvesByCode={shelvesByCode}
                  selectedCode={selectedShelf?.code}
                  onSelectShelf={(code, shelf) =>
                    setSelectedShelf({ code, shelf })
                  }
                />
              ))}

            {!showStorefrontMap && showSecondFloor && !sectionId && (
        <Row className="g-3">
          {SECOND_FLOOR_SECTIONS.map(section => (
            <Col key={section.id} xs={12} md={4}>
              <Card
                className="h-100 warehouse-location-card"
                role="button"
                tabIndex={0}
                onClick={() => setSectionId(section.id)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSectionId(section.id);
                  }
                }}
              >
                <Card.Body>
                  <div className="warehouse-floor-label warehouse-floor-label-red mb-2">
                    {section.label}
                  </div>
                  <div className="text-body-tertiary">
                    Open the {section.label} shelf map
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
          {otherShelves.length > 0 && (
            <Col xs={12} md={4}>
              <Card
                className="h-100 warehouse-location-card"
                role="button"
                tabIndex={0}
                onClick={() => setSectionId('other')}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSectionId('other');
                  }
                }}
              >
                <Card.Body>
                  <div className="fw-semibold fs-7 mb-2">Other shelves</div>
                  <div className="text-body-tertiary">
                    A / B and remaining warehouse shelves
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {!showStorefrontMap && showSecondFloor && sectionId && sectionId !== 'other' &&
        (shelvesLoading ? (
          <div className="text-center py-8">
            <Spinner animation="border" />
          </div>
        ) : (
          <SecondFloorMap
            sectionId={sectionId}
            shelvesByCode={shelvesByCode}
            selectedCode={selectedShelf?.code}
            onSelectShelf={(code, shelf) => setSelectedShelf({ code, shelf })}
          />
        ))}

      {!showStorefrontMap &&
        ((showSecondFloor && sectionId === 'other') || !showSecondFloor) &&
        (shelvesLoading ? (
          <div className="text-center py-8">
            <Spinner animation="border" />
          </div>
        ) : (
          <GenericShelfGrid
            shelves={showSecondFloor ? otherShelves : shelves}
            selectedCode={selectedShelf?.code}
            onSelectShelf={(code, shelf) => setSelectedShelf({ code, shelf })}
          />
        ))}
          </div>
          {selectedShelf && branchDomain && selectedLocation && (
            <ShelfItemsPanel
              branchDomain={branchDomain}
              locationId={selectedLocation.locationId}
              locationName={selectedLocation.locationName}
              shelfCode={selectedShelf.code}
              shelf={selectedShelf.shelf}
              onHide={() => setSelectedShelf(null)}
            />
          )}
        </div>
      )}
    </>
  );
};

export default LocationMapView;
